import { useState } from 'react';
import { FLAG_ICON, FLAG_LABEL, ROLE_LABEL } from '@doit/shared';
import { Lock, MessageSquarePlus, Phone, Trash2 } from 'lucide-react';
import { Button, Card, EmptyState, Spinner } from '../../components/ui';
import { Checkbox, Textarea } from '../../components/form';
import { Badge } from '../../components/badges';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../api/client';
import { useCurrentUser } from '../../api/auth';
import { useCreateNote, useDeleteNote, useStudentNotes } from '../../api/queries';
import { formatDate, formatDateTime } from '../../lib/format';

export function StudentNotes({
  studentId,
  groupId,
}: {
  studentId: string;
  groupId: string | undefined;
}) {
  const user = useCurrentUser();
  const toast = useToast();
  const { data: notes, isLoading } = useStudentNotes(studentId);
  const create = useCreateNote(studentId);
  const remove = useDeleteNote(studentId);

  const [text, setText] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);

  const isLecturer = user.role === 'LECTURER';
  const canWritePrivate = !isLecturer;
  const today = new Date().toISOString().slice(0, 10);

  async function submit() {
    if (!text.trim()) {
      toast.error('კომენტარი ცარიელია');
      return;
    }
    try {
      await create.mutateAsync({
        studentId,
        groupId: groupId ?? null,
        text,
        isPrivate,
        nextCallAt: null,
      });
      setText('');
      setIsPrivate(false);
      toast.success('კომენტარი დაემატა');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'ვერ დაემატა');
    }
  }

  async function handleDelete(id: string) {
    try {
      await remove.mutateAsync(id);
      toast.success('წაიშალა');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'წაშლა ვერ მოხერხდა');
    }
  }

  return (
    <Card title={`კომენტარები${notes ? ` (${notes.length})` : ''}`}>
      {/* --- ახალი --- */}
      <div className="mb-4 space-y-2.5">
        <Textarea
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={
            isLecturer
              ? 'კომენტარი მენეჯერისთვის…'
              : 'კომენტარი სტუდენტზე — მაგ. ჩამორჩენა, პროგრესი, შეთანხმება'
          }
        />

        <div className={`flex flex-wrap items-center gap-3 ${canWritePrivate ? 'justify-between' : 'justify-end'}`}>
          {canWritePrivate && (
            <Checkbox
              label="პირადი ჩანაწერი"
              checked={isPrivate}
              onChange={setIsPrivate}
              hint="ლექტორს არ უჩანს"
            />
          )}
          <Button
            size="sm"
            icon={<MessageSquarePlus className="size-4" />}
            onClick={() => void submit()}
            loading={create.isPending}
          >
            დამატება
          </Button>
        </div>
      </div>

      {/* --- სია --- */}
      {isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : (notes ?? []).length === 0 ? (
        <EmptyState
          title="კომენტარები ჯერ არ არის"
          description={
            isLecturer ? undefined : 'აქ ჩანს კომუნიკაციის ისტორია — ვინ, როდის და რა დააფიქსირა.'
          }
        />
      ) : (
        <ul className="max-h-[28rem] divide-y divide-line overflow-y-auto pr-1">
          {(notes ?? []).map((n) => {
            const canDelete = user.role === 'ADMIN' || n.author?.id === user.id;
            return (
              <li key={n.id} className="py-3">
                <div className="mb-1 flex flex-wrap items-center gap-2 text-[12px]">
                  <span className="font-medium text-ink">{n.author?.fullName ?? 'წაშლილი'}</span>
                  {n.author && <Badge>{ROLE_LABEL[n.author.role]}</Badge>}
                  {/* გაცდენების დაფაზე სტატუსთან ერთად დაწერილი კომენტარი */}
                  {n.flagType && (
                    <Badge tone="brand">
                      {FLAG_ICON[n.flagType]} {FLAG_LABEL[n.flagType]}
                    </Badge>
                  )}
                  {n.isPrivate && (
                    <Badge tone="warn">
                      <Lock className="mr-0.5 inline size-2.5" />
                      პირადი
                    </Badge>
                  )}
                  {n.group && <Badge tone="brand">{n.group.name}</Badge>}
                  {n.nextCallAt && (
                    <Badge tone={n.nextCallAt <= today ? 'danger' : 'ok'}>
                      <Phone className="mr-0.5 inline size-2.5" />
                      დარეკვა {formatDate(n.nextCallAt)}
                    </Badge>
                  )}
                  <span className="ml-auto text-ink-mute">{formatDateTime(n.createdAt)}</span>
                  {canDelete && (
                    <button
                      onClick={() => void handleDelete(n.id)}
                      aria-label="წაშლა"
                      className="rounded p-1 text-ink-mute transition hover:bg-danger-soft hover:text-danger"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  )}
                </div>
                <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-ink-soft">
                  {n.text}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
